import { Component, Input, Output, EventEmitter, OnInit, OnDestroy } from '@angular/core';

export interface StepDefinition {
  id?: string;
  label: string;
  icon?: string;
  statusBadge?: () => string;
  action: () => Promise<void> | void;
  isDone: () => boolean;
  isFailed?: () => boolean;
}

@Component({
  selector: 'app-step-progress-runner',
  templateUrl: './step-progress-runner.component.html',
  styleUrls: ['./step-progress-runner.component.css'],
})
export class StepProgressRunnerComponent implements OnInit, OnDestroy {
  emojis = [
    '🚀', '💡', '🧠', '🎨', '✨', '📝', '✍️', '📚', '📄', '🗒️',
    '📊', '📈', '💼', '💰', '🎯', '🔍', '🧭', '📋', '🧩', '🤝',
    '🎉', '✅', '⏳', '🏆', '🔥', '🌈', '💭', '💬', '📅', '📌',
  ];

  @Input() steps: StepDefinition[] = [];
  @Input() validationIntervalMs = 3000;
  @Input() maxAttempts = 100;
  @Input() resumeFromStepId?: string;
  @Input() startAtStep: number = 0;

  @Output() onStepChange = new EventEmitter<number>();
  @Output() onStepStart = new EventEmitter<number>();
  @Output() onStepDone = new EventEmitter<number>();
  @Output() onStepFailed = new EventEmitter<{ stepIndex: number; step: StepDefinition }>();
  @Output() onStepTimeout = new EventEmitter<{ stepIndex: number; step: StepDefinition }>();
  @Output() onAllDone = new EventEmitter<void>();

  currentStep = 0;
  isProcessing = false;
  isComplete = false;
  validationInterval: any = null;

  ngOnInit() {
    if (this.resumeFromStepId && this.steps.length > 0) {
      const idx = this.steps.findIndex((s) => s.id === this.resumeFromStepId);
      if (idx !== -1) {
        this.currentStep = idx;
      }
    } else if (this.startAtStep > 0 && this.startAtStep < this.steps.length) {
      this.currentStep = this.startAtStep;
    } else if (this.startAtStep >= this.steps.length && this.steps.length > 0) {
      this.currentStep = this.steps.length;
      this.completeAll();
      return;
    }

    if (this.steps.length > 0) {
      this.runCurrentStep();
    }
  }

  ngOnDestroy() {
    this.clearValidationInterval();
  }

  public retryCurrentStep(): void {
    this.runCurrentStep();
  }

  private clearValidationInterval(): void {
    if (this.validationInterval) {
      clearInterval(this.validationInterval);
      this.validationInterval = null;
    }
  }

  private async runCurrentStep(): Promise<void> {
    this.clearValidationInterval();

    if (this.currentStep >= this.steps.length) {
      this.completeAll();
      return;
    }

    const step = this.steps[this.currentStep];

    this.isProcessing = true;
    console.log(`[StepRunner] Starting step ${this.currentStep}: "${step.label}"`);
    this.onStepStart.emit(this.currentStep);

    try {
      await Promise.resolve(step.action());
      this.startValidationPolling(step);
    } catch (err) {
      console.error(`[StepRunner] Error executing step "${step.label}"`, err);
      this.isProcessing = false;
      this.onStepFailed.emit({ stepIndex: this.currentStep, step });
    }
  }

  private startValidationPolling(step: StepDefinition): void {
    let attempts = 0;
    this.clearValidationInterval();

    this.validationInterval = setInterval(() => {
      attempts++;
      console.log(`[StepRunner] Step "${step.label}" poll #${attempts}/${this.maxAttempts}`);

      if (step.isFailed && step.isFailed()) {
        console.error(`[StepRunner] Step "${step.label}" failed predicate check.`);
        this.clearValidationInterval();
        this.isProcessing = false;
        this.onStepFailed.emit({ stepIndex: this.currentStep, step });
        return;
      }

      const done = step.isDone();
      if (done) {
        console.log(`[StepRunner] Step "${step.label}" validated successfully on attempt ${attempts}.`);
        this.clearValidationInterval();
        this.isProcessing = false;

        this.onStepDone.emit(this.currentStep);

        setTimeout(() => {
          this.currentStep++;
          this.onStepChange.emit(this.currentStep);
          this.runCurrentStep();
        }, 1200); // smooth delay
        return;
      }

      if (attempts >= this.maxAttempts) {
        console.warn(
          `[StepRunner] Validation for step "${step.label}" timed out after ${attempts} attempts (${attempts * this.validationIntervalMs}ms).`
        );
        this.clearValidationInterval();
        this.isProcessing = false;
        this.onStepTimeout.emit({ stepIndex: this.currentStep, step });
      }
    }, this.validationIntervalMs);
  }

  private completeAll(): void {
    this.clearValidationInterval();
    this.isComplete = true;
    console.log('[StepRunner] All steps completed.');
    this.onAllDone.emit();
  }
}
